import { inject } from '@angular/core';
import { CanMatchFn, Router, UrlTree } from '@angular/router';
import { Store } from '@ngrx/store';
import { getIsLoggedIn } from '@schaeffler/azure-auth';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

const DEFAULT_AUTHENTICATED_ROUTE = '/welcome-header';

const resolveReturnUrl = (segments: readonly { path: string }[]): string => {
  const attemptedPath = segments
    .map((segment) => segment.path.trim())
    .filter((segment) => segment.length > 0)
    .join('/');

  return attemptedPath.length > 0
    ? `/${attemptedPath}`
    : DEFAULT_AUTHENTICATED_ROUTE;
};

/**
 * Guards routes based on authentication state.
 *
 * @param store NGRX store used to read the current authentication state.
 * @param router Router used to build a redirect `UrlTree`.
 * @returns An observable that emits `true` when the user is logged in, otherwise
 * emits a `UrlTree` redirecting to `/login`.
 */
export const allowIfLoggedInOtherwiseRedirectToLogin = (
  store: Store,
  router: Router,
  returnUrl = DEFAULT_AUTHENTICATED_ROUTE,
): Observable<boolean | UrlTree> =>
  store.select(getIsLoggedIn).pipe(
    take(1),
    map((isLoggedIn) =>
      isLoggedIn
        ? true
        : router.createUrlTree(['/login'], {
            queryParams: { returnUrl },
          }),
    ),
  );

export const requireLoggedInCanMatch: CanMatchFn = (_, segments) => {
  const store = inject(Store);
  const router = inject(Router);

  return allowIfLoggedInOtherwiseRedirectToLogin(
    store,
    router,
    resolveReturnUrl(segments),
  );
};
